/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ListRegisteredSettingsParams,
  TLHTTPValidationError,
  TLPaginatedResponseRegisteredSettingResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Registered Settings
 */
export const listRegisteredSettings = (
  params?: ListRegisteredSettingsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseRegisteredSettingResponse>(
    { url: `/settings-registry`, method: 'GET', params, signal },
    options,
  );
};

export const getListRegisteredSettingsQueryKey = (params?: ListRegisteredSettingsParams) => {
  return ['settings-registry', ...(params ? [params] : [])] as const;
};

export const getListRegisteredSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof listRegisteredSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListRegisteredSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRegisteredSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListRegisteredSettingsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listRegisteredSettings>>> = ({ signal }) =>
    listRegisteredSettings(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listRegisteredSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListRegisteredSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof listRegisteredSettings>>>;
export type ListRegisteredSettingsQueryError = ErrorType<TLHTTPValidationError>;

export function useListRegisteredSettings<
  TData = Awaited<ReturnType<typeof listRegisteredSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListRegisteredSettingsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRegisteredSettings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRegisteredSettings>>,
          TError,
          Awaited<ReturnType<typeof listRegisteredSettings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRegisteredSettings<
  TData = Awaited<ReturnType<typeof listRegisteredSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListRegisteredSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRegisteredSettings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRegisteredSettings>>,
          TError,
          Awaited<ReturnType<typeof listRegisteredSettings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRegisteredSettings<
  TData = Awaited<ReturnType<typeof listRegisteredSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListRegisteredSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRegisteredSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Registered Settings
 */

export function useListRegisteredSettings<
  TData = Awaited<ReturnType<typeof listRegisteredSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListRegisteredSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRegisteredSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListRegisteredSettingsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Registered Settings
 */
export const prefetchListRegisteredSettingsQuery = async <
  TData = Awaited<ReturnType<typeof listRegisteredSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListRegisteredSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRegisteredSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListRegisteredSettingsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Registered Settings
 */
export const invalidateListRegisteredSettings = async (
  queryClient: QueryClient,
  params?: ListRegisteredSettingsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListRegisteredSettingsQueryKey(params) }, options);

  return queryClient;
};
